import { LOCALE_ID, Injector, APP_INITIALIZER, EnvironmentProviders, Provider, importProvidersFrom } from '@angular/core';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { registerLocaleData } from '@angular/common';
import { Observable, from } from 'rxjs';

import { LocaleService } from '@app/core/ngx-translate/local.service';
import { AppConfigService } from 'app/core/config/app-config.service';
import { baseLanguage } from 'app/core/i18n/language-names';

// Import locale data
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeHu from '@angular/common/locales/hu';
import localeSv from '@angular/common/locales/sv';
import localeCs from '@angular/common/locales/cs';
import localeSk from '@angular/common/locales/sk';
import localeRu from '@angular/common/locales/ru';
import localeJa from '@angular/common/locales/ja';
import localeTr from '@angular/common/locales/tr';
import localeZh from '@angular/common/locales/zh';
import localeRo from '@angular/common/locales/ro';
import localeNb from '@angular/common/locales/nb';
import localeHe from '@angular/common/locales/he';
import localeIt from '@angular/common/locales/it';

// Register locales once globally
registerLocaleData(localeDe);
registerLocaleData(localeEs);
registerLocaleData(localeEn);
registerLocaleData(localeFr);
registerLocaleData(localeHu);
registerLocaleData(localeSv);
registerLocaleData(localeCs);
registerLocaleData(localeSk);
registerLocaleData(localeRu);
registerLocaleData(localeJa);
registerLocaleData(localeTr);
registerLocaleData(localeZh);
registerLocaleData(localeRo);
registerLocaleData(localeNb);
registerLocaleData(localeHe);
registerLocaleData(localeIt);

/**
 * Loads the UI texts from the API first: it serves the translators' saved work, which a
 * frontend upload cannot overwrite. The bundled asset file is only the fallback. Uses
 * fetch() so no interceptor shows errors or adds headers.
 */
export class VersionedTranslateLoader implements TranslateLoader {
  constructor(private appConfig: AppConfigService) {}

  getTranslation(lang: string): Observable<object> {
    return from(this.load(lang));
  }

  private async load(lang: string): Promise<object> {
    await this.appConfig.load();
    const api = this.appConfig.get<string>('serverUrl', '');
    const base = baseLanguage(lang);
    // A language without any texts yet (e.g. es-CO) must still start the app: use its base,
    // and otherwise nothing, so the English default fills in.
    for (const code of base !== lang ? [lang, base] : [lang]) {
      const version = localStorage.getItem('i18n_version_' + code) || '0';
      const texts = (api ? await fetchCatalog(`${api}/v1/translations/bundle/${code}`) : null)
        ?? await fetchCatalog(`./assets/i18n-studema/${code}.json?v=${version}`);
      if (texts) {
        return texts;
      }
    }
    return {};
  }
}

/** The JSON object at url, or null on errors and on the HTML page the server returns (with 200) for missing files. */
export async function fetchCatalog(url: string): Promise<object | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data && typeof data === 'object' && !Array.isArray(data) ? data : null;
  } catch {
    return null;
  }
}

export function createTranslateLoader(appConfig: AppConfigService): TranslateLoader {
  return new VersionedTranslateLoader(appConfig);
}

/**
 * Function to get the locale based on NGX-Translate's current language
 */
export function getLocaleId(translate: TranslateService, appConfig?: AppConfigService): string {
  const lang = translate.currentLang || appConfig?.get('defaultLanguage', 'en') || 'en';
  switch (lang) {
    case 'de':
      return 'de-DE';
    case 'es':
      return 'es-ES';
    case 'fr':
      return 'fr-FR';
    default:
      return 'en-US';
  }
}

/**
 * Function to initialize translation service before the app starts
 */

export function initializeTranslation(injector: Injector): () => Promise<void> {
  return () => {
    const translateService = injector.get(TranslateService);
    const localeService = injector.get(LocaleService);

    // Initialize language from localStorage
    localeService.initializeLanguage();

    return new Promise<void>((resolve) => {
      translateService.onLangChange.subscribe((event) => {
        console.log('Language switched:', event.lang);
        resolve();
      });
    });
  };
}

/**
 * Provides the ngx-translate configuration for Angular's provider system
 */
export const provideNgxTranslate = (): Array<Provider | EnvironmentProviders> => [
  importProvidersFrom(
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [AppConfigService],
      },
      defaultLanguage: 'en',
    })
  ),
  {
    provide: APP_INITIALIZER,
    useFactory: initializeTranslation,
    deps: [Injector],
    multi: true,
  },
  {
    provide: LOCALE_ID,
    useFactory: (localeService: LocaleService) => localeService.getLocaleFromLanguage(localStorage.getItem('user_language') || 'en'),
    deps: [LocaleService],
  },
];
